import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticationApiService } from '@warema/shared/authentication/api';
import { SunCenterService } from '@warema/shared/services';
import { TenantConfigurationService } from '@warema/shared/tenant-config';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { MenuService } from './menu/menu.service';

enum HeaderImgType {
    DEFAULT = 'assets/images/header.jpg',
    SHOP = 'assets/images/header_shop.jpg',
}

@Injectable({
    providedIn: 'root',
})
export class AppStateService {
    public readonly translateService = inject(TranslateService);
    public readonly menuService = inject(MenuService);
    private readonly _router = inject(Router);
    private readonly _sunCenterService = inject(SunCenterService);
    private readonly _tenantConfig = inject(TenantConfigurationService);
    private readonly _authenticationApiService = inject(AuthenticationApiService);

    public headerImgUrl$ = new BehaviorSubject<string>(HeaderImgType.DEFAULT);
    public headerUrl$ = new BehaviorSubject<string>('/');
    public openedSubmenu$ = new BehaviorSubject<string>('');
    public showPlaceholderLoader$ = new BehaviorSubject<boolean>(false);

    public currentRoute$ = new BehaviorSubject<string>('');

    public countrySearch$ = new BehaviorSubject<string>('');
    public isSideMenuExtended$ = new BehaviorSubject<boolean>(true);
    public isShopFabricsOrBrochuresRoute$ = new BehaviorSubject<boolean>(false);
    public isOrderHistoryRoute$ = new BehaviorSubject<boolean>(false);
    public isQrCodeRedirectRoute$ = new BehaviorSubject<boolean>(false);

    constructor() {
        this._handleRouterEvents();
        this._tenantConfig.settings$.pipe(first()).subscribe(data => {
            if (data?.country) {
                this._setLanguageFromSunCenter();
            }
        });
    }

    private _handleRouterEvents(): void {
        this._router.events.subscribe(val => {
            if (val instanceof NavigationEnd) {
                this.currentRoute$.next(val.url);
                this._initShopDesign(val.url);
                this._handleHeaderUrl(val.url);
                this.menuService.activateMenuByUrl(val.url);
                this._handleQrCodeRedirect(val.url);
            }
        });
    }

    private _setLanguageFromSunCenter(): void {
        this._sunCenterService.sunCenterLang$.pipe(filter(x => !!x)).subscribe((lang: string) => {
            lang = lang.toLowerCase();
            const sunCenterLanguages = ['de', 'en', 'fr', 'nl'];
            const defaultSunCenterLanguage = 'de';
            const availableLangs = this.translateService.getLangs();
            const currentCountry = this._tenantConfig.settings.country;
            const langMappingDictionary: Map<string, string> = new Map([
                ['de', 'de-DE'],
                ['en', 'en-GB'],
                ['fr', 'fr-FR'],
                ['nl', 'nl-NL'],
            ]);

            const computedLang = `${lang}-${currentCountry}`;

            if (availableLangs.includes(computedLang)) {
                this.setLanguage(computedLang);
            } else {
                if (!sunCenterLanguages.includes(lang)) {
                    lang = defaultSunCenterLanguage;
                }
                const mappedLanguage = langMappingDictionary.get(lang);
                this.setLanguage(mappedLanguage);
            }
        });
    }

    public getOpenSubmenu(): Observable<string> {
        return this.openedSubmenu$.asObservable();
    }

    public getCurrentRoute(): Observable<string> {
        return this.currentRoute$.asObservable();
    }

    public setIsSideMenuExtended(isExtended: boolean): void {
        this.isSideMenuExtended$.next(isExtended);
    }

    public setLanguage(language: string): void {
        this.translateService.use(language);
    }

    private _handleHeaderUrl(url: string): void {
        if (url.includes('/shop')) {
            this.headerUrl$.next('/shop');
        } else {
            this.headerUrl$.next('/');
        }
    }

    private _initShopDesign(url: string): void {
        const isShopRoute = url.startsWith('/shop');
        const isFabricBrochure = url.startsWith('/shop/brochures') || url.startsWith('/shop/fabrics');
        const isOrderHistoryRoute = url.startsWith('/order-history');
        this.isShopFabricsOrBrochuresRoute$.next(isFabricBrochure);
        this.isOrderHistoryRoute$.next(isOrderHistoryRoute);
        this.menuService.hideSideMenu$.next(isShopRoute || !this._authenticationApiService.loggedIn$.getValue());
        this.headerImgUrl$.next(isShopRoute ? HeaderImgType.SHOP : HeaderImgType.DEFAULT);
    }

    private _handleQrCodeRedirect(url: string): void {
        const isQrCodeRedirectRoute = url.startsWith('/qr-code-redirect');
        this.menuService.hideSideMenu$.next(
            isQrCodeRedirectRoute || !this._authenticationApiService.loggedIn$.getValue(),
        );
        this.isQrCodeRedirectRoute$.next(isQrCodeRedirectRoute);
    }
}
